<template>
  <section class="client-cabinet-plain-section" data-testid="client-page-history" aria-labelledby="client-page-history-title">
    <div class="client-cabinet-section-heading">
      <div>
        <h2 id="client-page-history-title" data-testid="client-page-history-title">
          {{ $t("client_page.cabinet.history.title", { count: items.length }) }}
        </h2>
        <p v-if="items.length">{{ $t("client_page.cabinet.history.operations", { count: items.length }) }}</p>
      </div>
    </div>
      <p v-if="!items.length" class="client-cabinet-empty-state">{{ $t("client_page.cabinet.history.empty") }}</p>
      <ul v-else id="client-page-history-list" class="client-cabinet-list">
        <!-- Две линии: способ — сумма; дата · операция — статус. Сумма и статус держатся справа на любой ширине. -->
        <li
          v-for="item in visibleItems"
          :key="item.key"
          class="client-cabinet-history__item"
          data-testid="client-page-history-item"
          :data-field="item.fields"
        >
          <div class="client-cabinet-history__line client-cabinet-history__line--main">
            <span class="client-cabinet-history__title">{{ item.title }}</span>
            <strong class="client-cabinet-history__amount">{{ item.amount }}</strong>
          </div>
          <div class="client-cabinet-history__line client-cabinet-history__line--sub text-body-2">
            <span class="client-cabinet-history__meta client-cabinet-muted">{{ item.date }}<template v-if="item.note"> · {{ item.note }}</template></span>
            <span class="client-cabinet-history__status">{{ item.status }}</span>
          </div>
        </li>
      </ul>
      <button
        v-if="items.length > previewCount"
        type="button"
        class="client-cabinet-disclosure"
        data-testid="client-page-disclosure-history"
        :aria-expanded="String(showAll)"
        aria-controls="client-page-history-list"
        @click="showAll = !showAll"
      >
        {{ $t(showAll ? "client_page.cabinet.history.hide" : "client_page.cabinet.history.show_all", { count: items.length }) }}
      </button>
  </section>
</template>

<script>
// FT-200 STEP-05 (REQ-08): «История платежей (N)» — один список из платежей бронирования и платежей провайдеров
// клиента (Stripe, Yandex Pay, Т-Банк), новые сверху, статус — словами. Суммы Т-Банка нового ответа — рубли строкой.
import { translateCountCategory } from "@/utils/countCategory";
import {
  countMethodKey, formatCabinetDate, money, parseAmount, PROVIDER_STATUS_KEYS, PROVIDER_STATUS_UNKNOWN_KEY, TINKOFF_OUTCOME_KEYS,
  TINKOFF_PURPOSE_KEYS,
} from "@/utils/clientCabinetView";

const COUNT_FIELDS = "booking.counts[].sum booking.counts[].operation booking.counts[].completed booking.counts[].created_at booking.counts[].group booking.counts[].cash booking.counts[].cashless booking.counts[].cash_card booking.counts[].entity booking.counts[].client_balance";
const providerFields = (list) => ["order_id", "status", "amount", "refunded_amount", "created_at"].map((f) => `${list}[].${f}`).join(" ");
const TINKOFF_FIELDS = ["purpose", "outcome", "amount", "refunded_amount", "card_number", "created_at"].map((f) => `tinkoff_payments[].${f}`).join(" ");

export default {
  name: "PaymentHistory",
  props: {
    booking: { type: Object, required: true },
    company: { type: Object, default: null },
    stripePayments: { type: Array, default: () => [] },
    yandexPayPayments: { type: Array, default: () => [] },
    tinkoffPayments: { type: Array, default: () => [] },
  },
  data() {
    return { previewCount: 3, showAll: false };
  },
  computed: {
    symbol() {
      return (this.company && this.company.currency_symbol) || "";
    },
    items() {
      const rows = [];
      (this.booking.counts || []).forEach((count, index) => {
        const method = countMethodKey(count);
        rows.push({
          key: `count-${index}`,
          at: count.created_at,
          fields: COUNT_FIELDS,
          title: this.translateCategory(count.group) || this.$t("client_page.cabinet.history.kinds.count"),
          note: method ? this.$t(method) : "",
          amount: `${count.operation ? "" : "−"}${money(count.sum, this.symbol)}`,
          status: this.$t(count.completed ? "client_page.cabinet.history.count_completed" : "client_page.cabinet.history.count_pending"),
        });
      });
      this.providerRows(rows, this.stripePayments, "stripe_payments", "client_page.cabinet.history.kinds.stripe", this.symbol);
      this.providerRows(rows, this.yandexPayPayments, "yandex_pay_payments", "client_page.cabinet.history.kinds.yandex_pay", "₽");
      (this.tinkoffPayments || []).forEach((payment, index) => {
        const purpose = TINKOFF_PURPOSE_KEYS[payment.purpose];
        rows.push({
          key: `tinkoff-${index}`,
          at: payment.created_at,
          fields: TINKOFF_FIELDS,
          title: this.$t(purpose || "client_page.cabinet.history.kinds.tinkoff"),
          note: [payment.card_number, this.refundNote(payment.refunded_amount, "₽")].filter(Boolean).join(" · "),
          // Т-Банк — рубли.
          amount: money(payment.amount, "₽"),
          status: this.$t(TINKOFF_OUTCOME_KEYS[payment.outcome] || "client_page.cabinet.history.outcomes.unknown"),
        });
      });
      rows.forEach((row) => { row.date = this.formatDate(row.at); });
      return rows.sort((a, b) => (Date.parse(b.at) || 0) - (Date.parse(a.at) || 0));
    },
    visibleItems() {
      return this.showAll ? this.items : this.items.slice(0, this.previewCount);
    },
  },
  methods: {
    providerRows(rows, payments, list, titleKey, symbol) {
      (payments || []).forEach((payment, index) => {
        rows.push({
          key: `${list}-${index}`,
          at: payment.created_at,
          fields: providerFields(list),
          title: this.$t(titleKey),
          note: [
            payment.order_id ? this.$t("client_page.cabinet.history.order", { id: payment.order_id }) : "",
            this.refundNote(payment.refunded_amount, symbol),
          ].filter(Boolean).join(" · "),
          amount: money(payment.amount, symbol),
          status: this.$t(PROVIDER_STATUS_KEYS[payment.status] || PROVIDER_STATUS_UNKNOWN_KEY),
        });
      });
    },
    refundNote(value, symbol) {
      return parseAmount(value) > 0 ? this.$t("client_page.cabinet.history.refunded", { amount: money(value, symbol) }) : "";
    },
    translateCategory(category) {
      return translateCountCategory((key) => this.$t(key), category);
    },
    formatDate(date) {
      return formatCabinetDate(date, (this.$i18n && this.$i18n.locale) || "ru");
    },
  },
};
</script>

<style scoped>
.client-cabinet-history__item {
  padding: 12px 0;
}

.client-cabinet-history__line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  column-gap: 16px;
}

.client-cabinet-history__line--sub {
  margin-top: 2px;
}

.client-cabinet-history__title,
.client-cabinet-history__meta {
  min-width: 0;
  overflow-wrap: anywhere;
}

.client-cabinet-history__amount,
.client-cabinet-history__status {
  flex-shrink: 0;
  white-space: nowrap;
  text-align: right;
}
</style>
